// CHG-123 REQ-V123-032: 이슈 타입 선택 모달
import { useEffect } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import { useProjectIssueTypes, type ProjectIssueType } from '../../hooks/useProjectIssueTypes';
import { resolveIssueTypeIcon } from '../../constants/issueTypeConfig';
import Spinner from '../ui/Spinner';

interface MessageIssueTypePickerProps {
  open: boolean;
  projectId: string;
  projectName?: string;
  onClose: () => void;
  onBack: () => void;
  onSelect: (issueType: ProjectIssueType) => void;
}

export default function MessageIssueTypePicker({
  open, projectId, projectName, onClose, onBack, onSelect,
}: MessageIssueTypePickerProps) {
  // ESC로 닫기
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open, onClose]);

  const { data, isLoading, isError } = useProjectIssueTypes(projectId);

  if (!open) return null;

  const types = data?.issueTypes ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="issuetype-picker-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button onClick={onBack} className="p-1 text-gray-400 hover:text-gray-600 rounded" aria-label="뒤로">
              <ArrowLeft className="size-5" />
            </button>
            <div>
              <h3 id="issuetype-picker-title" className="text-lg font-semibold text-gray-900">작업 유형 선택</h3>
              {projectName && <p className="text-sm text-gray-500">{projectName}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded" aria-label="닫기">
            <X className="size-5" />
          </button>
        </div>

        {/* 결과 */}
        {isLoading && (
          <div className="flex justify-center py-6">
            <Spinner className="size-6" />
          </div>
        )}

        {isError && !isLoading && (
          <p className="text-sm text-red-500 py-4 text-center">작업 유형 목록을 불러오지 못했습니다.</p>
        )}

        {!isLoading && !isError && types.length === 0 && (
          <p className="text-sm text-gray-400 py-6 text-center">사용 가능한 작업 유형이 없습니다.</p>
        )}

        {types.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {types.map((t) => {
              const { Icon } = resolveIssueTypeIcon({ issueTypeId: t.issueTypeId, name: t.name, icon: t.icon, color: t.color });
              return (
                <button
                  key={t.issueTypeId}
                  onClick={() => onSelect(t)}
                  className="flex items-center gap-2 px-3 py-3 border border-gray-200 rounded-lg hover:bg-primary-50 hover:border-primary-300 transition-colors text-left"
                >
                  <Icon className="size-5 shrink-0" style={{ color: t.color }} />
                  <span className="text-sm font-medium text-gray-900 truncate">{t.name}</span>
                  {t.isDefault && <span className="text-[11px] text-gray-400 ml-auto">기본</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
